import { Box, GlobalStyles, Stack } from '@mui/material';
import { type RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { useResizable } from 'react-resizable-layout';
import { LiveTranscriptHeader } from 'src/components/Assistant/live-transcript/live-transcript-header';
import { LiveTranscriptMessages } from 'src/components/Assistant/live-transcript/live-transcript-messages';
import { LiveTranscriptSearchHeader } from 'src/components/Assistant/live-transcript/live-transcript-search-header';
import {
  liveTranscriptDrawerContainerSx,
  liveTranscriptResizeHandleSx,
} from 'src/components/Assistant/live-transcript/live-transcript.styles';
import { LiveTranscriptDrawerProps } from 'src/components/Assistant/live-transcript/live-transcript.types';
import {
  buildFullTranscriptText,
  buildTranscriptSearchIndex,
  groupSegmentsIntoParagraphs,
} from 'src/components/Assistant/live-transcript/live-transcript.utils';
import { useLiveTranscription } from 'src/components/Assistant/live-transcript/use-live-transcription';
import { useCopyToClipboard } from 'src/utils/hooks';
import useDebouncedValue from 'src/utils/hooks/use-debounced-value';

const MIN_HEIGHT_PERCENT = 20;
const MAX_HEIGHT_PERCENT = 80;
const DEFAULT_HEIGHT_PERCENT = 50;
const DEFAULT_DRAWER_HEIGHT_PX = 320;
const MIN_DRAWER_HEIGHT_PX = 140;

interface DrawerHeightLimits {
  initial: number;
  min: number;
  max: number;
}
const BOTTOM_SCROLL_STICKINESS_THRESHOLD_PX = 40;

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

const computeDrawerHeightLimits = (parentHeight: number): DrawerHeightLimits => {
  const rawMin = Math.max((parentHeight * MIN_HEIGHT_PERCENT) / 100, MIN_DRAWER_HEIGHT_PX);
  const rawMax = (parentHeight * MAX_HEIGHT_PERCENT) / 100;
  const min = Math.min(rawMin, rawMax);
  const max = Math.max(rawMin, rawMax);
  const initial = clamp((parentHeight * DEFAULT_HEIGHT_PERCENT) / 100, min, max);

  return { initial, min, max };
};

export const LiveTranscriptDrawer = ({
  enabled,
  conversationId,
  partnerId,
  onVisibilityChange,
}: LiveTranscriptDrawerProps) => {
  const { copyToClipboard: copyTranscriptToClipboard } = useCopyToClipboard({
    successMessage: 'Transcript copied',
  });
  const { copyToClipboard: copyMessageToClipboard } = useCopyToClipboard({
    successMessage: 'Message copied',
  });
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 150);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [heightLimits, setHeightLimits] = useState<DrawerHeightLimits>({
    initial: DEFAULT_DRAWER_HEIGHT_PX,
    min: MIN_DRAWER_HEIGHT_PX,
    max: DEFAULT_DRAWER_HEIGHT_PX,
  });
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const resizeContainerRef = useRef<HTMLElement | null>(null);
  const hasAppliedInitialPositionRef = useRef(false);
  const shouldStickToBottomRef = useRef(true);
  const {
    position: drawerHeightPx,
    setPosition,
    separatorProps,
    isDragging,
  } = useResizable({
    axis: 'y',
    reverse: true,
    initial: heightLimits.initial,
    min: heightLimits.min,
    max: heightLimits.max,
    containerRef: resizeContainerRef as RefObject<HTMLElement>,
  });

  const { segments, isFinished, isStreaming, transcriptionId } = useLiveTranscription({
    enabled,
    conversationId,
    partnerId,
  });

  const hasContent = segments.length > 0;
  const isVisible = enabled && (hasContent || isFinished);

  useEffect(() => {
    onVisibilityChange?.(isVisible);
  }, [isVisible, onVisibilityChange]);

  useEffect(() => {
    if (!isVisible) {
      return;
    }

    const parent = containerRef.current?.parentElement;

    if (!parent) {
      return;
    }

    resizeContainerRef.current = parent;

    const applyLimits = (parentHeight: number) => {
      if (parentHeight <= 0) {
        return;
      }

      const limits = computeDrawerHeightLimits(parentHeight);

      setHeightLimits(limits);

      if (!hasAppliedInitialPositionRef.current) {
        setPosition(limits.initial);
        hasAppliedInitialPositionRef.current = true;
        return;
      }

      setPosition((current) => clamp(current, limits.min, limits.max));
    };

    applyLimits(parent.clientHeight);

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }
      applyLimits(entry.contentRect.height);
    });

    observer.observe(parent);

    return () => {
      observer.disconnect();
    };
  }, [isVisible, setPosition]);

  useEffect(() => {
    shouldStickToBottomRef.current = true;
  }, [conversationId, partnerId, transcriptionId]);

  useEffect(() => {
    if (isCollapsed || !scrollRef.current || !hasContent) {
      return;
    }

    if (!shouldStickToBottomRef.current) {
      return;
    }

    if (isSearchOpen && debouncedSearchQuery) {
      return;
    }

    scrollRef.current.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [hasContent, isCollapsed, segments, isSearchOpen, debouncedSearchQuery]);

  const transcriptText = useMemo(() => buildFullTranscriptText(segments), [segments]);

  const groupedSegments = useMemo(() => groupSegmentsIntoParagraphs(segments), [segments]);
  const { totalMatches, matchOffsetBySegmentId } = useMemo(
    () => buildTranscriptSearchIndex(groupedSegments, debouncedSearchQuery),
    [groupedSegments, debouncedSearchQuery],
  );

  const effectiveMatchIndex =
    totalMatches === 0 ? 0 : Math.min(currentMatchIndex, totalMatches - 1);

  useEffect(() => {
    const container = scrollRef.current;
    if (!isSearchOpen || !debouncedSearchQuery || totalMatches === 0 || !container) {
      return;
    }
    const target = container.querySelector<HTMLElement>(
      `[data-match-index="${effectiveMatchIndex}"]`,
    );
    if (!target) {
      return;
    }
    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const offset =
      targetRect.top - containerRect.top - container.clientHeight / 2 + targetRect.height / 2;
    container.scrollBy({ top: offset, behavior: 'smooth' });
  }, [effectiveMatchIndex, isSearchOpen, debouncedSearchQuery, totalMatches, segments]);

  const handleOpenSearch = () => {
    if (isCollapsed) {
      setIsCollapsed(false);
    }
    setIsSearchOpen(true);
  };

  const handleCloseSearch = () => {
    setIsSearchOpen(false);
    setSearchQuery('');
    setCurrentMatchIndex(0);
  };

  const handleSearchQueryChange = (next: string) => {
    setSearchQuery(next);
    setCurrentMatchIndex(0);
  };

  const handlePrevMatch = () => {
    if (totalMatches === 0) return;
    setCurrentMatchIndex((effectiveMatchIndex - 1 + totalMatches) % totalMatches);
  };

  const handleNextMatch = () => {
    if (totalMatches === 0) return;
    setCurrentMatchIndex((effectiveMatchIndex + 1) % totalMatches);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <>
      {isDragging ? (
        <GlobalStyles
          styles={{
            body: {
              userSelect: 'none',
              cursor: 'ns-resize',
            },
          }}
        />
      ) : null}
      <Stack
        ref={containerRef}
        sx={liveTranscriptDrawerContainerSx(isCollapsed, drawerHeightPx, isDragging)}
      >
        {!isCollapsed ? (
          <Box
            role="separator"
            aria-label="Resize live transcript drawer"
            {...separatorProps}
            sx={liveTranscriptResizeHandleSx}
          />
        ) : null}

        {isSearchOpen ? (
          <LiveTranscriptSearchHeader
            searchQuery={searchQuery}
            totalMatches={totalMatches}
            currentMatchIndex={effectiveMatchIndex}
            onSearchQueryChange={handleSearchQueryChange}
            onPrevMatch={handlePrevMatch}
            onNextMatch={handleNextMatch}
            onCloseSearch={handleCloseSearch}
          />
        ) : (
          <LiveTranscriptHeader
            isCollapsed={isCollapsed}
            isFinished={isFinished}
            isStreaming={isStreaming}
            canCopy={hasContent}
            canSearch={hasContent}
            onExpandFromHeader={() => setIsCollapsed(false)}
            onToggleCollapse={() => setIsCollapsed((previous) => !previous)}
            onCopyTranscript={() => void copyTranscriptToClipboard(transcriptText)}
            onOpenSearch={handleOpenSearch}
          />
        )}

        {!isCollapsed ? (
          <LiveTranscriptMessages
            groupedSegments={groupedSegments}
            isStreaming={isStreaming}
            scrollRef={scrollRef}
            onScroll={(event) => {
              const target = event.currentTarget;
              const distance = target.scrollHeight - (target.scrollTop + target.clientHeight);

              shouldStickToBottomRef.current = distance < BOTTOM_SCROLL_STICKINESS_THRESHOLD_PX;
            }}
            onCopySegment={(text) => void copyMessageToClipboard(text)}
            searchQuery={debouncedSearchQuery}
            currentMatchIndex={effectiveMatchIndex}
            matchOffsetBySegmentId={matchOffsetBySegmentId}
          />
        ) : null}
      </Stack>
    </>
  );
};
